import { isWhatuni } from "./commonFunction";
import { getCustomDomain } from "./common-function-server";

export type SubjectProfileUrlParams = {
  deptTextKey: string;
  keyProfileId: string | number;
  institutionId: string | number;
  institutionTextkey: string;
};

export type ProviderResultUrlParams = {
  subjectTextKey?: string;
  institutionTextkey: string;
  isClearing?: Boolean;
  isPostgraduate?: Boolean;
  hasUgCourses?: boolean;
};
export type OpendaysParams = {
  institutionId: string | number;
  institutionTextkey: string;
};

export type ScholarshipDetailParams = {
  scholarshipId: string | number;
  institutionTextkey: string;
  scholarshipName: string;
};

export type CDUrlParams = {
  courseTextKey?: string;
  courseId?: string | number;
  institutionId?: string | number;
  institutionTextkey?: string;
};
export const generateSubjectProfileUrl = ({
  deptTextKey,
  keyProfileId,
  institutionId,
  institutionTextkey,
}: SubjectProfileUrlParams): string => {
  if (!deptTextKey && !keyProfileId && !institutionId && !institutionTextkey) {
    return "";
  }
  const encodedDeptTextKey = encodeURIComponent(String(deptTextKey));
  const encodedInstitutionTextkey = encodeURIComponent(
    String(institutionTextkey),
  );
  const encodedInstitutionId = encodeURIComponent(String(institutionId));
  const encodedKeyProfileId = encodeURIComponent(String(keyProfileId));

  return isWhatuni()
    ? `/degrees/subject-profile/${encodedDeptTextKey}/${encodedInstitutionTextkey}/${encodedInstitutionId}/${encodedKeyProfileId}/`
    : `/${encodedInstitutionTextkey}/cp-${encodedKeyProfileId}`;
};

export const generateProviderResultUrl = ({
  subjectTextKey,
  institutionTextkey,
  isClearing,
  isPostgraduate = false,
  hasUgCourses,
}: ProviderResultUrlParams): string => {
  if (!institutionTextkey) return "";
  const baseURL = getCustomDomain();

  const getQualPath = (): string => {
    if (isPostgraduate) return "/postgraduate-courses";
    if (hasUgCourses === false) return "/all-courses";
    return "/degree-courses";
  };

  const base = isWhatuni()
    ? `${baseURL}${getQualPath()}/csearch`
    : `${baseURL}/pgs/search`;

  const params: string[] = [];
  if (isClearing && isWhatuni()) {
    params.push("clearing");
  }
  if (subjectTextKey) {
    const key = isWhatuni() ? "subject" : "course";
    params.push(`${key}=${encodeURIComponent(String(subjectTextKey))}`);
  }
  params.push(`university=${encodeURIComponent(String(institutionTextkey))}`);
  return `${base}?${params.join("&")}`;
};

export const generateOpendayUrl = ({
  institutionId,
  institutionTextkey,
}: OpendaysParams): string => {
  if (!institutionId && !institutionTextkey) {
    return "";
  }
  const baseURL = getCustomDomain();
  const encodedInstitutionTextkey = encodeURIComponent(
    String(institutionTextkey),
  );
  const encodedInstitutionId = encodeURIComponent(String(institutionId));
  return isWhatuni()
    ? `${baseURL}/open-days/${encodedInstitutionTextkey}/${encodedInstitutionId}/`
    : `${baseURL}/${encodedInstitutionTextkey}/opendays`;
};

export const generateStudentReviewsUrl = ({
  institutionId,
  institutionTextkey,
}: OpendaysParams): string => {
  if (!institutionId && !institutionTextkey) {
    return "";
  }
  const baseURL = getCustomDomain();
  const encodedInstitutionTextkey = encodeURIComponent(
    String(institutionTextkey),
  );
  const encodedInstitutionId = encodeURIComponent(String(institutionId));
  return isWhatuni()
    ? `${baseURL}/university-course-reviews/${encodedInstitutionTextkey}/${encodedInstitutionId}/`
    : `${baseURL}/${encodedInstitutionTextkey}/university-reviews.htm`;
};

export const generateScholarshipsDetailsRouting = ({
  scholarshipId,
  institutionTextkey,
  scholarshipName,
}: ScholarshipDetailParams): string => {
  function toSlug(str?: string): string {
    if (!str) return "";
    return str
      .toLowerCase()
      .replace(/\s+/g, "-") // replace spaces with hyphen
      .replace(/[^a-z0-9-]/g, ""); // remove non-alphanumeric except "-"
  }
  const baseURL = getCustomDomain();
  const nameSlug = toSlug(scholarshipName);
  if (process.env.PROJECT === "PGS") {
    return `${baseURL}/funding/${nameSlug}/${institutionTextkey}/${scholarshipId}/`;
  } else if (process.env.PROJECT === "Whatuni") {
    return `${baseURL}/money/scholarships/${nameSlug}/${institutionTextkey}/${scholarshipId}/`;
  } else {
    return `${baseURL}/scholarship-details/${scholarshipId}/`; // default fallback
  }
};

export const generateProfilePageUrl = ({
  institutionId,
  institutionTextkey,
}: OpendaysParams): string => {
  if (!institutionId && !institutionTextkey) {
    return "";
  }
  const baseURL = getCustomDomain();
  const encodedInstitutionTextkey = encodeURIComponent(
    String(institutionTextkey),
  );
  const encodedInstitutionId = encodeURIComponent(String(institutionId));
  return isWhatuni()
    ? `${baseURL}/university-profile/${encodedInstitutionTextkey}/${encodedInstitutionId}/`
    : `${baseURL}/universities/${encodedInstitutionTextkey}/`;
};

export const cleanUrl = (url: string): string => {
  // Fix slash placement around query params and decode special characters
  let result = url
    ?.replace("//", "/")
    ?.replace("??", "?")
    ?.replace("/?", "?") // Remove slash before query params
    ?.replace("?/", "/") // Remove slash after query params
    ?.replaceAll("%2B", "+") // Decode plus signs
    ?.replaceAll("%2C", ","); // Decode commas

  // Add trailing slash if no query params present

  if (
    (result?.includes("?") || result?.includes("&")) &&
    result?.endsWith("/")
  ) {
    result = result.slice(0, -1);
  }

  if (
    !result?.includes("?") &&
    !result?.includes("=") &&
    !result?.endsWith("/")
  ) {
    result += "/";
  }
  if (result?.endsWith("?")) {
    result = result.slice(0, -1) + "/";
  }
  return result;
};

export const generateCDPageUrl = ({
  courseTextKey,
  courseId,
  institutionId,
  institutionTextkey,
}: CDUrlParams): string => {
  if (!institutionId && !institutionTextkey) {
    return "";
  }
  const baseURL = getCustomDomain();
  const encodedInstitutionTextkey = encodeURIComponent(
    String(institutionTextkey),
  );
  return isWhatuni()
    ? `${baseURL}/degrees/${courseTextKey}/${encodedInstitutionTextkey}/cd/${courseId}/${institutionId}/`
    : `${baseURL}/courses/search/postgraduate/${encodedInstitutionTextkey}/${courseTextKey}/${courseId}/`;
};
